// Die offenen Fragen fürs ganze CRM (E3, S6; Gernots Entscheidung 2: kein
// Web-Push – dafür stehen offene Fragen oben, sichtbar, nicht zu übersehen).
// EINMAL geholt, im Rahmen (rahmen.tsx), und an alle verteilt: das Band oben
// auf jeder Seite, die Zahl am Eintrag „Fragen“ in der Seitenleiste und die
// Seite „Fragen“ selbst – so sagen alle drei immer dasselbe.
//
// Wann neu geholt wird:
//  - beim Laden;
//  - nach jeder eigenen Aktion – Frage, Antwort, „erledigt“ melden das über
//    speicher.ts (`anderenFensternMelden`), auch einem zweiten Fenster;
//  - seit E3c, S2 (O69): sobald sich der Stand der offenen Fragen ändert.
//    Den fragt der Rahmen jede Minute mit den Zahlen der Seitenleiste (GET
//    /api/crm/zaehler, nur solange der Tab vorne ist) – Fragen kommen auch
//    von draußen (Doku-App); die ganze Liste nur, wenn sich etwas geändert
//    hat. Vorher holte diese Datei die ganze Liste jede Minute selbst.
// Geht es einmal nicht (Netz weg), bleibt die letzte Liste stehen – das Band
// soll nicht flackern; die Seite „Fragen“ zeigt den Fehler.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { FrageMitKontext, FragenAntwort } from "@happy-energy/kern";
import { holen } from "@happy-energy/oberflaeche";
import { useAmServerGeaendert } from "../speicher.ts";

/** So oft fragt das CRM nach, solange es vorne ist. */
export const NACHFRAGEN_MS = 60_000;

export interface OffeneFragen {
  /** Die offenen Fragen, älteste zuerst – null: noch nicht geladen. */
  fragen: FrageMitKontext[] | null;
  /** Was beim letzten Holen schiefging – sonst undefined. Die alte Liste bleibt stehen. */
  fehler: unknown;
  /** Gleich noch einmal holen. */
  neu: () => void;
}

const NICHTS: OffeneFragen = { fragen: null, fehler: undefined, neu: () => undefined };

export const OffeneFragenKontext = createContext<OffeneFragen>(NICHTS);

/** Die offenen Fragen, wie der Rahmen sie hält – außerhalb des Rahmens: keine. */
export function useOffeneFragen(): OffeneFragen {
  return useContext(OffeneFragenKontext);
}

/**
 * Holt die offenen Fragen – nur im Rahmen aufgerufen. `an`: Darf die Rolle
 * Fragen sehen? `fragenStand`: der Stand aus den Zahlen der Seitenleiste
 * (null: noch keiner) – ändert er sich, wird neu geholt.
 */
export function useOffeneFragenHolen(an: boolean, fragenStand: string | null): OffeneFragen {
  const [stand, setStand] = useState<{ fragen: FrageMitKontext[] | null; fehler: unknown }>({ fragen: null, fehler: undefined });
  const [takt, setTakt] = useState(0);
  const neu = useCallback(() => setTakt((n) => n + 1), []);
  /** Der zuletzt gesehene Stand – der erste nach dem Laden holt nicht noch einmal. */
  const zuletzt = useRef<string | null>(null);

  // Eine Frage, Antwort oder „erledigt“ – hier oder in einem anderen Fenster.
  useAmServerGeaendert(neu);

  // Von draußen oder von jemand anderem im Büro: Der Stand in den Zahlen hat sich geändert.
  useEffect(() => {
    if (fragenStand === null) return;
    if (zuletzt.current !== null && zuletzt.current !== fragenStand) neu();
    zuletzt.current = fragenStand;
  }, [fragenStand, neu]);

  useEffect(() => {
    if (!an) return;
    let gilt = true;
    holen<FragenAntwort>("/api/crm/fragen?offen=1").then(
      (a) => {
        if (gilt) setStand({ fragen: a.fragen, fehler: undefined });
      },
      (fehler: unknown) => {
        if (gilt) setStand((alt) => ({ fragen: alt.fragen, fehler }));
      },
    );
    return () => {
      gilt = false;
    };
  }, [an, takt]);

  return useMemo(() => (an ? { fragen: stand.fragen, fehler: stand.fehler, neu } : NICHTS), [an, stand, neu]);
}
